import { actions, ChartKeyRequest, ChartStore, CHARTS_STORE_NAME, IStore } from './store';
import { frontendActions, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { webTradingApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';
import { getConfig } from '@cmctechnology/webinvest-store-frontend';
import { IChartDataVeGet1Response, IChartRequest } from './charts/simple/models';
import { IChartProRequest, IChartDataProChartGet1Response } from './charts/pro/models';
import { formChartsApiQuery, transformChartResponse, getChartKeysFromQueries } from './charts/simple/utils';
import { formChartsProApiQuery, transformChartProResponse, getChartProKeysFromQueries } from './charts/pro/utils';
import { DEFAULT_TIMEZONE, getChartTypeFromParam, getTimeZoneFromParam } from './utils';
import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';

/** Multiple API types  */
export const API_NAMES = {
  ChartDataVeGet1: `ChartDataVeGet1`, // Simple
  ChartDataProChartGet1: `ChartDataProChartGet1` // Pro
} as const;

export const chartTypeApiMap = {
  simple: API_NAMES.ChartDataVeGet1,
  pro: API_NAMES.ChartDataProChartGet1
} as const;

export type ChartType = keyof typeof chartTypeApiMap;

type ResourceKey = number | string;

type StoreDispatch = typeof ChartStore.dispatch;

export const getChartData = (key: ChartKeyRequest) => async (dispatch: StoreDispatch) => {
  const chartType = getChartTypeFromParam(key);
  const timeZone = chartType === `pro` ? getTimeZoneFromParam(key) : '';

  const apiName = chartTypeApiMap[chartType] ?? API_NAMES.ChartDataVeGet1;
  const request = chartType === `simple` ? formChartsApiQuery(key) : formChartsProApiQuery(key);

  const callChartDataAPI = async () => {
    return webTradingApi(getConfig().stockbrokingApiUrl).send({ [apiName]: request }, false);
  };

  try {
    const response = await frontendActions.apiCall(CHARTS_STORE_NAME, apiName, callChartDataAPI, key)(dispatch);
    if (chartType === `simple`) {
      const transformed = transformChartResponse(request.Instrument, response as IChartDataVeGet1Response);
      dispatch(
        actions.chartDataUpdated({
          key,
          response: transformed
        })
      );
      return transformed;
    } else if (chartType === `pro`) {
      const transformed = transformChartProResponse(request.Instrument, response as IChartDataProChartGet1Response, timeZone);
      dispatch(
        actions.chartDataUpdated({
          key,
          response: transformed
        })
      );
      return transformed;
    }
  } catch {}
};

// TODO: move this action to webinvest-store-exchange
// TODO: check if the data is already available in the store before calling api
export const fetchMarketHour = () => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const callCountriesandEchangesAPI = async () => {
        return webTradingApi(getConfig().stockbrokingApiUrl).send(
          {
            ['CountriesandExchangesGet1']: {}
          },
          false
        );
      };
      const responseExchangeId = (await frontendActions.apiCall(
        CHARTS_STORE_NAME,
        'CountriesandExchangesGet1',
        callCountriesandEchangesAPI
      )(dispatch)) as CmcWebApi.MarketInformation.CountriesandExchangesModel1;

      const exchangeArray = responseExchangeId.Exchanges.map((item: any) => item.ExchangeId);

      const callMarketHoursAPI = async () => {
        return webTradingApi(getConfig().stockbrokingApiUrl).send(
          {
            ['MarketHoursGet1']: {
              ExchangeIds: exchangeArray
            }
          },
          false
        );
      };
      const response = await frontendActions.apiCall(CHARTS_STORE_NAME, 'MarketHoursGet1', callMarketHoursAPI)(dispatch);
      dispatch(
        actions.marketHourFetched({
          response: response as CmcWebApi.MarketInformation.MarketHoursModel1[]
        })
      );
    } catch {}
  };
};

// TODO: update with the new push API implementation
// https://github.com/cmctechnology/marketdata-candles/pull/27#discussion_r793258032
// https://cmcmarkets.atlassian.net/wiki/spaces/SEAP/pages/3643375654/API
// eslint-disable-next-line unicorn/consistent-function-scoping
export const onPoll =
  (subscriptions: ResourceKey[] = []) =>
  (dispatch: Dispatch<any>) => {
    // Note: `subscriptions` are provided by default
    // this can be accessed by storeKey in the frontend store as well
    // This loop can be replaced by a single api call that supports array of subscriptions
    subscriptions.forEach((sub) => {
      dispatch(getChartData(sub as ChartKeyRequest));
    });
  };

type Query = IChartRequest | IChartRequest[] | IChartProRequest | IChartProRequest[];

export const subscribe =
  (location: LocationType, query: Query, timeZone: string = DEFAULT_TIMEZONE, chartType: ChartType = 'simple', pollTimeout = getConfig().pollTimeout) =>
  (dispatch: Dispatch<any>, getState: () => IStore) => {
    const resourceKeys =
      chartType === 'simple' ? getChartKeysFromQueries(query as IChartRequest) : getChartProKeysFromQueries(query as IChartProRequest, timeZone);
    const hasStoreSubscription = resourceKeys.find((key) => getState()[CHARTS_STORE_NAME][key]);

    /* istanbul ignore next */
    if (!hasStoreSubscription && pollTimeout > getConfig().pollTimeout) {
      /* istanbul ignore next */
      dispatch(onPoll(resourceKeys));
    }

    dispatch(
      frontendActions.subscribe({
        onPoll,
        location,
        apiName: chartTypeApiMap[chartType],
        storeName: CHARTS_STORE_NAME,
        resourceKeys,
        pollTimeout
      })
    );
  };

export const unsubscribe =
  (location: LocationType, query: Query, timeZone: string = DEFAULT_TIMEZONE, chartType: ChartType = 'simple') =>
  (dispatch: Dispatch<any>) => {
    const resourceKeys =
      chartType === 'simple' ? getChartKeysFromQueries(query as IChartRequest) : getChartProKeysFromQueries(query as IChartProRequest, timeZone);
    dispatch(
      frontendActions.unsubscribe({
        location,
        apiName: chartTypeApiMap[chartType],
        storeName: CHARTS_STORE_NAME,
        resourceKeys
      })
    );
  };
